import type { VariantProps } from "class-variance-authority"
import type { ComponentPropsWithoutRef, ReactNode } from "react"
import React from "react"
import { Link } from "@tanstack/react-router"
import { cva } from "class-variance-authority"
import hexToRgba from "hex-to-rgba"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"

import type { TagsGet } from "@productlane/api"
import { cn } from "@productlane/lib/src/browser"
import { Tooltip } from "@productlane/ui"

import { getProjectIcon } from "@/lib/projects"
import { hexToRGB } from "./color-picker"
import { RoleCheck } from "./shared/role-check"

export const tagVariants = cva(
	"group flex flex-none items-center gap-2 py-0.5 rounded-full bg-opacity-5 pr-1 pl-0.5 text-primary select-none",
	{
		variants: {
			variant: {
				default: "",
				transparent: "",
				// A tag the system suggests, not one a teammate applied. Slash alphas
				// ignore the base bg-opacity; 1px, since the house .5px dashes barely show.
				suggested:
					"border-[1px] border-dashed border-gray-300 bg-transparent opacity-60 outline-none hover:bg-gray-50/100 hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-gray-300 dark:border-gray-600 dark:hover:bg-gray-750/100 dark:focus-visible:ring-gray-600",
			},
			size: {
				default: "",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
)

interface TagProps
	extends
		Omit<ComponentPropsWithoutRef<"div">, "children">,
		VariantProps<typeof tagVariants> {
	tag: Pick<TagsGet[number], "id" | "name" | "color" | "icon">
	groupColor?: string
	onClose?: () => void
	isLink?: boolean
	showLinkButton?: boolean
	/** Shown on hover over the name; defaults to the name. */
	tooltip?: ReactNode
	/** Accessible name for the close button, with a tooltip when set. */
	closeLabel?: string
}

export const Tag = React.memo(
	React.forwardRef<HTMLDivElement, TagProps>(
		(
			{
				className,
				size,
				isLink = false,
				variant = "default",
				tag,
				groupColor,
				onClose,
				showLinkButton,
				tooltip,
				closeLabel,
				onClick,
				onKeyDown,
				...props
			},
			ref,
		) => {
			const Icon = getProjectIcon(tag.icon)
			const color = groupColor ?? tag.color
			const content = (
				// eslint-disable-next-line jsx-a11y/no-static-element-interactions -- a role would change what screen readers announce
				<div
					ref={ref}
					className={cn(tagVariants({ variant, size, className }))}
					style={
						variant === "suggested"
							? undefined
							: {
									backgroundColor:
										variant === "default"
											? hexToRGB(color, "bg")
											: "transparent",
									border: `0.5px solid ${hexToRgba(color, "0.2")}`,
								}
					}
					onClick={showLinkButton ? undefined : onClick}
					onKeyDown={
						showLinkButton
							? undefined
							: onKeyDown ||
								(onClick
									? (e) => {
											if (e.key === "Enter" || e.key === " ") {
												e.preventDefault()
												onClick(
													e as unknown as React.MouseEvent<HTMLDivElement>,
												)
											}
										}
									: undefined)
					}
					{...props}
				>
					<div className="relative ml-1">
						<Icon
							className={cn(
								"h-3.5 w-3.5 flex-none transition-opacity duration-150",
								onClose && "group-hover:opacity-0",
							)}
							style={{ color }}
						/>
						<RoleCheck not={["VIEWER"]}>
							{onClose && (
								<Tooltip label={closeLabel} enabled={!!closeLabel}>
									<button
										type="button"
										aria-label={closeLabel ?? "Remove tag"}
										className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100"
										style={{ color }}
										onClick={(e) => {
											e.preventDefault()
											e.stopPropagation()
											onClose()
										}}
									>
										<X className="h-3.5 w-3.5 flex-none" />
									</button>
								</Tooltip>
							)}
						</RoleCheck>
					</div>
					<Tooltip label={tooltip ?? tag.name}>
						<div className="flex-1 truncate pr-1.5 text-left text-xs font-medium">
							{tag.name}
						</div>
					</Tooltip>
				</div>
			)
			if (isLink || showLinkButton) {
				return (
					<Link
						to="/tags/$id"
						params={{ id: tag.id }}
						className="inline-block"
						onClick={(e) => {
							const target = e.target as HTMLElement
							if (
								target.closest("button") ||
								target.closest('[role="button"]')
							) {
								e.preventDefault()
								e.stopPropagation()
								return
							}
							if (onClick) {
								onClick(e as unknown as React.MouseEvent<HTMLDivElement>)
							}
						}}
						onKeyDown={(e) => {
							e.stopPropagation()
						}}
					>
						{content}
					</Link>
				)
			}
			return content
		},
	),
)
